import { useEffect } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import type { 
  SyncPayload, 
  ExtensionResponse, 
  SyncResponse 
} from '../types/extension';

// ID de la extensión (inyectado vía Vite config)
const EXTENSION_ID = import.meta.env.VITE_EXTENSION_ID || 'iobnadjkbhmdoboancpnmglacknijlbk';

export const ExtensionSync = () => {
  const { isSignedIn, getToken } = useAuth();
  const { user } = useUser();

  useEffect(() => {
    // 1. Proactive synchronization when auth state changes
    const syncToken = async () => {
      if (isSignedIn && user) {
        try {
          const token = await getToken();
          if (token) {
            console.log("📢 [Phoenix] Detectada sesión activa, sincronizando...");

            const payload: SyncPayload = { 
              type: "SYNC_CLERK_SESSION", 
              token: token, 
              user: {
                id: user.id,
                email: user.primaryEmailAddress?.emailAddress,
                fullName: user.fullName,
                imageUrl: user.imageUrl,
              } 
            };

            // Method 1: PostMessage (Content Script)
            // We always send this in case there's an active content script listening
            window.postMessage(payload, "*");

            // Method 2: Direct (externally_connectable)
            // We check if chrome.runtime is available to avoid errors in non-Chrome browsers
            if (typeof chrome !== 'undefined' && chrome.runtime?.sendMessage) {
              try {
                chrome.runtime.sendMessage(EXTENSION_ID, payload, (response: ExtensionResponse) => {
                  const error = chrome.runtime.lastError;
                  if (error) {
                    if (error.message?.includes("Could not establish connection")) {
                      console.warn("⚠️ [Phoenix] La extensión no parece estar instalada o no permite conexión externa.");
                    } else if (error.message?.includes("context invalidated")) {
                      console.error("❌ [Phoenix] Contexto de la extensión invalidado. Por favor, recarga la página.");
                    } else {
                      console.error("❌ [Phoenix] Error en comunicación directa:", error.message);
                    }
                    return;
                  }
                  console.log("📢 [Phoenix] Sincronización directa exitosa:", response);
                });
              } catch (e) {
                console.error("❌ [Phoenix] Fallo crítico al intentar contactar con la extensión:", e);
              }
            }
          }
        } catch (err) {
          console.error("❌ [Phoenix] Error sincronizando token:", err);
        }
      }
    };

    syncToken();

    // 2. Event-based Credit Sync (Phoenix Bridge)
    const handleSyncCredits = async (event: CustomEvent<{ credits?: number }>) => {
      if (!isSignedIn || typeof chrome === 'undefined' || !chrome.runtime?.sendMessage) return;
      
      console.log("💰 [Phoenix] Disparando SYNC_CREDITS a la extensión...");
      const token = await getToken();
      
      const payload: SyncPayload = {
        type: 'SYNC_CREDITS',
        token,
        credits: event.detail?.credits
      };

      chrome.runtime.sendMessage(EXTENSION_ID, payload, (response: ExtensionResponse) => {
        if (chrome.runtime?.lastError) return;
        console.log("💰 [Phoenix] Créditos sincronizados:", response);
      });
    };

    // Listen for global event to trigger from Success.tsx or other components
    window.addEventListener('VOZFLOW_SYNC_CREDITS', handleSyncCredits);

    // 3. Reactive synchronization: Listen for requests from the extension
    const handleMessage = (
      message: SyncPayload, 
      _sender: chrome.runtime.MessageSender, 
      sendResponse: (response?: SyncResponse) => void
    ) => {
      if (message.type === 'GET_CLERK_SESSION' && isSignedIn) {
        getToken().then((token: string | null) => {
          if (token) {
            sendResponse({ 
              token, 
              user: {
                id: user?.id || '',
                email: user?.primaryEmailAddress?.emailAddress,
                fullName: user?.fullName,
                imageUrl: user?.imageUrl,
              }
            });
          } else {
            sendResponse({ error: 'no_token' });
          }
        }).catch(() => {
          sendResponse({ error: 'clerk_error' });
        });
        return true; // Keep message channel open for async response
      }
      return false;
    };
    
    if (typeof chrome !== 'undefined' && chrome.runtime?.onMessage) {
      chrome.runtime.onMessage.addListener(handleMessage);
      return () => {
        chrome.runtime.onMessage.removeListener(handleMessage);
        window.removeEventListener('VOZFLOW_SYNC_CREDITS', handleSyncCredits);
      };
    }
    
    return () => {
      window.removeEventListener('VOZFLOW_SYNC_CREDITS', handleSyncCredits);
    };
  }, [isSignedIn, user, getToken]);

  return null; // Headless component
};